import { motion, useMotionValue, useTransform, type PanInfo } from 'motion/react';
import { useState } from 'react';
import type { Flashcard } from './FlashcardsView';
import styles from './FlashcardsView.module.css';

type Verdict = 'know' | 'dont-know';

type Props = {
  card: Flashcard;
  onDismiss: (verdict: Verdict) => void;
  offset?: number;
  isTop: boolean;
};

const DISMISS_THRESHOLD = 120;

export function Card({ card, onDismiss, offset = 0, isTop }: Props) {
  const [flipped, setFlipped] = useState(false);
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 0, 200], [-12, 0, 12]);
  const knowOpacity = useTransform(x, [40, 140], [0, 1]);
  const dontKnowOpacity = useTransform(x, [-140, -40], [1, 0]);

  const onDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.x > DISMISS_THRESHOLD) onDismiss('know');
    else if (info.offset.x < -DISMISS_THRESHOLD) onDismiss('dont-know');
  };

  return (
    <motion.div
      drag={isTop ? 'x' : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.65}
      onDragEnd={onDragEnd}
      style={{
        x: isTop ? x : 0,
        rotate: isTop ? rotate : 0,
        zIndex: isTop ? 3 : 1
      }}
      initial={{ scale: 1 - offset * 0.06, y: offset * 8, opacity: offset > 1 ? 0 : 1 }}
      animate={{ scale: 1 - offset * 0.06, y: offset * 8, opacity: offset > 1 ? 0 : 1 }}
      transition={{ type: 'spring', stiffness: 200, damping: 22 }}
      className={styles.cardWrap}
      onClick={() => isTop && setFlipped((f) => !f)}
      role={isTop ? 'button' : undefined}
      tabIndex={isTop ? 0 : -1}
      aria-label={isTop ? (flipped ? 'Mostrar pregunta' : 'Mostrar respuesta') : undefined}
      onKeyDown={(e) => {
        if (!isTop) return;
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          setFlipped((f) => !f);
        }
      }}
    >
      <div className={styles.flipper} data-flipped={flipped}>
        <div className={styles.face}>
          <span className={styles.faceLabel}>Pregunta</span>
          <p className={styles.faceText}>{card.front}</p>
          <span className={styles.hint}>Toca para ver la respuesta</span>
        </div>
        <div className={`${styles.face} ${styles.back}`}>
          <span className={styles.faceLabel}>Respuesta</span>
          <p className={styles.faceText}>{card.back}</p>
          <span className={styles.hint}>Arrastra → si te la sabes</span>
        </div>
      </div>

      {isTop && (
        <>
          <motion.div className={`${styles.stamp} ${styles.stampKnow}`} style={{ opacity: knowOpacity }}>
            Me la sé
          </motion.div>
          <motion.div className={`${styles.stamp} ${styles.stampNope}`} style={{ opacity: dontKnowOpacity }}>
            Repasar
          </motion.div>
        </>
      )}
    </motion.div>
  );
}
